import React, { useState, useEffect } from 'react';
import { Phone, Mail, Menu, X, ArrowRight, ShieldCheck } from 'lucide-react';
import Logo from './Logo';

const Navbar = ({ onOpenContact }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 30);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', href: '#hero' },
    { name: 'About Us', href: '#about' },
    { name: 'Services', href: '#services' },
    { name: 'Cost Estimator', href: '#calculator' },
    { name: 'Projects', href: '#projects' },
    { name: 'Equipment', href: '#equipment' },
  ];

  return (
    <header
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 900,
        transition: 'all 0.3s ease',
        background: scrolled ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderBottom: '1px solid #e2e8f0',
        boxShadow: scrolled ? '0 10px 30px -10px rgba(15, 23, 42, 0.08)' : 'none',
      }}
    >
      {/* Top Notification Bar */}
      <div
        style={{
          background: 'linear-gradient(90deg, #cc0000 0%, #e63946 40%, #0066ff 100%)',
          padding: '0.35rem 1rem',
          fontSize: '0.8rem',
          fontWeight: '600',
          color: '#ffffff',
        }}
      >
        <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <ShieldCheck size={14} /> Digital Land Survey & Consulting
            </span>
            <span style={{ opacity: 0.7 }} className="desktop-only">•</span>
            <span style={{ opacity: 0.95 }} className="desktop-only">
              Theni District, Tamil Nadu
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem' }}>
            <a
              href="tel:+919677579748"
              style={{ color: '#ffffff', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
            >
              <Phone size={13} /> +91 96775 79748
            </a>
            <a
              href="mailto:bagavathiraja96@gmail.com"
              style={{ color: '#ffffff', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
              className="desktop-only"
            >
              <Mail size={13} /> bagavathiraja96@gmail.com
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1.5rem' }}>
        {/* Brand Logo */}
        <a href="#hero" style={{ textDecoration: 'none' }}>
          <Logo size="medium" showText={true} lightMode={true} />
        </a>

        {/* Desktop Links */}
        <nav style={{ display: 'none', lgDisplay: 'flex' }} className="nav-desktop">
          <ul style={{ display: 'flex', gap: '2rem', listStyle: 'none', margin: 0, padding: 0 }}>
            {navLinks.map((link) => (
              <li key={link.name}>
                <a
                  href={link.href}
                  style={{
                    color: '#334155',
                    textDecoration: 'none',
                    fontWeight: '600',
                    fontSize: '0.95rem',
                    transition: 'color 0.2s ease',
                  }}
                  onMouseEnter={(e) => (e.target.style.color = '#e63946')}
                  onMouseLeave={(e) => (e.target.style.color = '#334155')}
                >
                  {link.name}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Action Button & Hamburger */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            onClick={() => onOpenContact()}
            className="btn-primary btn-glow"
            style={{ padding: '0.65rem 1.35rem', fontSize: '0.875rem' }}
          >
            Get Free Quote <ArrowRight size={15} />
          </button>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="mobile-toggle-btn"
            style={{
              background: '#f1f5f9',
              border: '1px solid #cbd5e1',
              color: '#0f172a',
              borderRadius: '8px',
              padding: '0.5rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            aria-label="Toggle Menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Drawer Menu */}
      {mobileMenuOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            borderBottom: '1px solid #e2e8f0',
            padding: '1.5rem',
            animation: 'fadeIn 0.2s ease-in-out',
            boxShadow: '0 20px 30px rgba(0,0,0,0.1)',
          }}
        >
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {navLinks.map((link) => (
              <li key={link.name}>
                <a
                  href={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  style={{
                    color: '#0f172a',
                    textDecoration: 'none',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    display: 'block',
                  }}
                >
                  {link.name}
                </a>
              </li>
            ))}
            <li style={{ paddingTop: '0.5rem', borderTop: '1px solid #e2e8f0' }}>
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  onOpenContact();
                }}
                className="btn-primary"
                style={{ width: '100%', justifyContent: 'center' }}
              >
                Request Instant Quote
              </button>
            </li>
          </ul>
        </div>
      )}

      <style>{`
        @media (min-width: 992px) {
          .nav-desktop { display: flex !important; }
          .mobile-toggle-btn { display: none !important; }
          .desktop-only { display: inline !important; }
        }
        @media (max-width: 991px) {
          .desktop-only { display: none !important; }
        }
      `}</style>
    </header>
  );
};

export default Navbar;
